import { useEffect, useCallback } from 'react';
import { auth } from '../../firebase';
import { useQuickLinks } from '../../contexts/QuickLinksContext';

const QuickLinksManager = () => {
    const { updateQuickLinks, clearQuickLinks, updateEnhancedRightClick, updateEmailVerified, updateEmailVerifiedLoading, updateNotificationPreferences, updateSnapshotFrequency } = useQuickLinks();

    const fetchQuickLinks = useCallback(async (user) => {
        try {
            const token = await user.getIdToken();

            // First check if enhanced right-click is enabled
            const userRes = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${user.uid}/`, {
                headers: {
                    'auth-token': `Bearer ${token}`,
                    'Authorization': `${token}`
                }
            });

            if (userRes.ok) {
                const userData = await userRes.json();
                const enhancedEnabled = userData.enhanced_right_click_enabled;
                const emailVerified = userData.email_verified;
                const snapshotFrequency = userData.snapshot_frequency_minutes || 5; // Default to 5 minutes

                // Update the global state with the preferences
                updateEnhancedRightClick(enhancedEnabled);
                updateEmailVerified(emailVerified);
                updateEmailVerifiedLoading(false); // Done loading
                updateSnapshotFrequency(snapshotFrequency);

                // Update notification preferences from user data
                const notifPrefs = {
                    emailNotifications: userData.email_notifications_enabled ?? true,
                    commentNotifications: userData.comment_notifications_enabled ?? true,
                    documentNotifications: userData.document_notifications_enabled ?? true,
                    subscriptionNotifications: userData.subscription_notifications_enabled ?? true,
                    workspaceNotifications: userData.workspace_management_notifications_enabled ?? true,
                    mentionNotifications: userData.mention_notifications_enabled ?? true,
                    reminderNotifications: userData.reminder_notifications_enabled ?? true,
                    exportNotifications: userData.export_notifications_enabled ?? true
                };
                updateNotificationPreferences(notifPrefs);

                if (!enhancedEnabled) {
                    // User has disabled enhanced right-click, clear quick links
                    clearQuickLinks();
                    return;
                }
            } else {
                // If we can't fetch user data, assume disabled
                updateEnhancedRightClick(false);
                updateEmailVerified(false);
                updateEmailVerifiedLoading(false); // Done loading even if failed
                clearQuickLinks();
                return;
            }

            // If enhanced right-click is enabled, fetch quick links
            const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${user.uid}/quick-links/`, {
                headers: {
                    'auth-token': `Bearer ${token}`,
                    'Authorization': `${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                updateQuickLinks(data);
            } else {
                // If API fails, clear the quick links
                clearQuickLinks();
            }
        } catch (error) {
            console.error('Error fetching quick links:', error);
            updateEnhancedRightClick(false);
            updateEmailVerified(false);
            updateEmailVerifiedLoading(false); // Done loading even if error
            clearQuickLinks();
        }
    }, [updateQuickLinks, clearQuickLinks, updateEnhancedRightClick, updateEmailVerified, updateEmailVerifiedLoading, updateNotificationPreferences, updateSnapshotFrequency]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                fetchQuickLinks(user);
            } else {
                // For non-logged-in users, keep enhanced right-click enabled by default
                // Don't call updateEnhancedRightClick to preserve the default true value
                updateEmailVerifiedLoading(false); // Not loading for logged-out users
                clearQuickLinks();
            }
        });

        return () => unsubscribe();
    }, [fetchQuickLinks, clearQuickLinks, updateEmailVerifiedLoading]); // Now includes fetchQuickLinks which is memoized with useCallback

    // This component doesn't render anything
    return null;
};

export default QuickLinksManager;
